<template>
    <div class="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div class="flex flex-col items-start w-min">
            <label class="text-sm font-medium mb-1 text-gray-600 mr-2">{{ $t('orgDashboard.reports.criteria') }}</label>
            <select v-model="yAxis"
                class="border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-purple-200">
                <option v-for="param in parameters" :key="`y-${param}`" :value="param">
                    {{ param }}
                </option>
            </select>
        </div>

        <div class="overflow-x-auto mt-5">
            <table class="min-w-full border border-gray-200" style="table-layout: fixed">
                <thead>
                    <tr>
                        <th
                            class="name px-4 py-3 text-left font-semibold text-sm text-gray-600 border border-gray-200 bg-gray-50 w-[500px]">
                            {{ $t('orgDashboard.reports.talent') }}
                        </th>
                        <th
                            class="px-4 py-3 text-left font-semibold text-sm text-gray-600 border border-gray-200 bg-gray-50 w-[140px]">
                            {{ $t('orgDashboard.reports.role') }}
                        </th>
                        <th v-for="(header, index) in assessmentData.scoreHeaders" :key="index"
                            class="px-4 py-3 text-center font-semibold text-sm text-gray-600 border border-gray-200 bg-gray-50 whitespace-nowrap w-[140px]">
                            {{ header.toUpperCase() }}
                        </th>
                        <th
                            class="px-4 py-3 text-center font-semibold text-sm text-gray-600 border border-gray-200 bg-gray-50 w-[140px]">
                                {{ $t('Total') }}
                        </th>
                    </tr>
                </thead>

                <tbody>
                    <tr>
                        <td class="px-4 py-4 whitespace-nowrap border border-gray-200">
                            <div class="font-medium text-sm text-gray-700">{{ $t('orgDashboard.reports.weightage') }}</div>
                        </td>
                        <td class="px-4 py-4 whitespace-nowrap border border-gray-200"></td>
                        <td v-for="(header, index) in assessmentData.scoreHeaders" :key="index"
                            class="px-4 py-4 text-center border border-gray-200">
                            <div class="text-sm text-gray-600">
                                {{ assessmentData.weightages[index] }}
                            </div>
                        </td>
                        <td class="px-4 py-4 text-center whitespace-nowrap border border-gray-200">
                            <div class="text-sm text-gray-600">100</div>
                        </td>
                    </tr>

                    <tr v-for="(employee, empIndex) in assessmentData.employees" :key="empIndex">
                        <td class="px-4 py-4 border border-gray-200">
                            <div class="flex items-center">
                                <div class="flex-shrink-0 h-10 w-10">
                                    <img class="h-10 w-10 rounded-full" :src="employee.avatar" alt="" />
                                </div>
                                <div class="ml-4">
                                    <div class="font-medium text-sm text-gray-900">
                                        {{ employee.name }}
                                    </div>
                                </div>
                            </div>
                        </td>
                        <td class="px-4 py-4 border border-gray-200">
                            <div class="px-1 py-1 text-xs text-left text-gray-800 font-semibold">
                                {{ employee.role }}
                            </div>
                        </td>
                        <!-- UPDATED: Added title (tooltip) for AI remarks -->
                        <td v-for="(scoreObj, scoreIndex) in employee.scores" :key="scoreIndex"
                            class="px-4 py-4 text-center border border-gray-200">
                            <div class="inline-flex items-center justify-center w-5 h-5 rounded-full mx-auto cursor-help"
                                :class="getScoreColorClass(scoreObj.value)"
                                :title="scoreObj.remarks">
                                <span v-if="scoreObj.value === null || scoreObj.value === undefined"
                                    class="text-xs text-white font-bold">N/A</span>
                            </div>
                        </td>
                        <td class="px-4 py-4 text-center whitespace-nowrap border border-gray-200">
                            <div class="text-sm font-bold" :class="getScoreTextColorClass(employee.totalScore)">
                                {{ employee.totalScore }}
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div class="mt-6 flex items-center justify-start space-x-6">
            <div class="flex items-center">
                <div class="w-4 h-4 rounded-full bg-green-500 mr-2"></div>
                <span class="text-sm text-gray-600">{{ $t('orgDashboard.reports.good') }}</span>
            </div>
            <div class="flex items-center">
                <div class="w-4 h-4 rounded-full bg-primary mr-2"></div>
                <span class="text-sm text-gray-600">{{ $t('orgDashboard.reports.average') }}</span>
            </div>
            <div class="flex items-center">
                <div class="w-4 h-4 rounded-full bg-red-500 mr-2"></div>
                <span class="text-sm text-gray-600">{{ $t('orgDashboard.reports.poor') }}</span>
            </div>
            <div class="flex items-center">
                <div class="w-4 h-4 rounded-full bg-slate-300 mr-2 border border-gray-600"></div>
                <span class="text-sm text-gray-600">{{ $t('orgDashboard.reports.notApplicable') }}</span>
            </div>
        </div>
    </div>
</template>

<script>
import constants from "@/constants";
import { kvdrOrgConfig } from "@/assets/data/kvdrOrgConfigs.js";

export default {
    name: "TalentScorecard",

    data() {
        return {
            yAxis: "Fitment (Fit)",
            key: "fitmentData",
            kvdrConfig: {},
            parameters: [
                "Fitment (Fit)",
                "Performance (Drive)",
                "Succession (Secure)",
                "Appraisal (Self)",
            ],
            assessmentData: {
                scoreHeaders: [],
                weightages: [],
                employees: [],
            },
        };
    },
    watch: {
        async kvdrData(val) {
            if (val) {
                this.createTalentScorecardData();
            }
        },
        yAxis() {
            this.createTalentScorecardData();
        },
    },
    computed: {
        userId() {
            return this.$store.state.auth.user._id;
        },
        orgData() {
            return this.$store.state.organization.orgData;
        },
        kvdrData() {
            return this.$store.state.kvdr.selectedKVDR;
        },
        employeesData() {
            return this.$store.state.organization.employees;
        },
    },
    methods: {
        getUserById(userId) {
            if (!this.employeesData) return [];
            return this.employeesData.filter((user) => user.userId == userId);
        },
        createTalentScorecardData() {
            if (!this.orgData || !this.orgData._id) return;
            
            const kvdrConfig = kvdrOrgConfig(this.orgData._id);
            this.kvdrConfig = kvdrConfig;

            if (this.yAxis === "Fitment (Fit)") {
                this.key = "fitmentData";
            } else if (this.yAxis === "Performance (Drive)") {
                this.key = "performanceData";
            } else if (this.yAxis === "Succession (Secure)") {
                this.key = "successionData";
            } else {
                this.key = "appraisalData";
            }
            
            const configData = kvdrConfig[this.key] || [];

            this.assessmentData = {
                weightages: this.extractTalentCardWeightage(configData),
                scoreHeaders: this.extractTalentCardTitles(configData),
                employees: this.extractTalentCardUsers(),
            };
        },
        extractTalentCardUsers() {
            if (!this.orgData || !this.kvdrData || !this.kvdrData.kvdrRoles) return [];

            const kvdrConfig = this.kvdrConfig || kvdrOrgConfig(this.orgData._id);
            const result = [];
            
            for (const index in this.kvdrData.kvdrRoles) {
                const kvdrRoles = this.kvdrData.kvdrRoles[index];
                
                if (kvdrRoles.kvdrRoleProfiles && Array.isArray(kvdrRoles.kvdrRoleProfiles)) {
                    kvdrRoles.kvdrRoleProfiles.forEach((e) => {
                        const filteredUser = this.getUserById(e.userId);
                        
                        if (filteredUser && filteredUser.length > 0) {
                            const tmpUser = filteredUser[0];
                            if (!tmpUser) return;

                            const userDataToParse = e[this.key] || e[this.key.replace('Data', '_data')];
                            let userScore = this.extractUserScore(userDataToParse, kvdrConfig[this.key]);
                            
                            const userData = tmpUser.userData || {};
                            const firstName = userData.firstName || 'User';
                            
                            let avatarUrl = "";
                            if (userData.profilePictureUrl) {
                                avatarUrl = `${constants.s3Bucket}/editor/profile-pictures/${userData.profilePictureUrl}`;
                            } else {
                                avatarUrl = `https://hostedboringavatars.vercel.app/api/beam?name=${firstName}`;
                            }

                            // BUG FIX: Calculate weighted sum and round at the end
                            // Logic: sum of (Color Grade Value * Attribute Weight)
                            const rawTotal = userScore.reduce((sum, scoreObj) => {
                                return sum + (scoreObj.value * scoreObj.weight);
                            }, 0);
                            
                            const calculatedTotal = Math.round(rawTotal);

                            result.push({
                                name: tmpUser.fullName || `${firstName} ${userData.lastName || ''}`,
                                role: kvdrRoles.title,
                                avatar: avatarUrl,
                                scores: userScore,
                                totalScore: calculatedTotal,
                            });
                        }
                    });
                }
            }
            return result.sort((a, b) => a.name.localeCompare(b.name));
        },
        extractTalentCardTitles(data) {
            if (!data) return [];
            const result = [];
            for (const category in data) {
                if (Array.isArray(data[category])) {
                    data[category].forEach((item) => {
                        result.push(item.title);
                    });
                }
            }
            return result;
        },
        // UPDATED: Now returns an object containing value and remarks/reasoning
        extractUserScore(userProfileData, configData) {
            if (!configData) return [];
            const result = [];
            
            for (const category in configData) {
                if (Array.isArray(configData[category])) {
                    configData[category].forEach(attr => {
                        const compositeKey = `${category}|${attr.key}`;
                        const match = (userProfileData || []).find(d => 
                            d.key === compositeKey || d.key === attr.key
                        );

                        // Capture the weight from the config (attr.value)
                        const weight = parseFloat(attr.value) || 0;

                        if (match) {
                            const val = parseFloat(match.score !== undefined ? match.score : match.value);
                            result.push({
                                value: isNaN(val) ? 0 : val,
                                weight: weight, // Capture weight here
                                remarks: match.remarks || match.reasoning || "No Remarks available"
                            });
                        } else {
                            result.push({ value: 0, weight: weight, remarks: "" });
                        }
                    });
                }
            }
            return result;
        },

        extractTalentCardWeightage(data) {
            if (!data) return [];
            const result = [];
            for (const category in data) {
                if (Array.isArray(data[category])) {
                    data[category].forEach((item) => {
                        result.push(item.value);
                    });
                }
            }
            return result;
        },

        getScoreColorClass(score) {
            if (!this.kvdrConfig || !this.kvdrConfig.calculationTypes) return "bg-gray-100";
            
            const calcs = this.kvdrConfig.calculationTypes;
            if (score === null || score === undefined) {
                return "bg-slate-300 border border-gray-400";
            }
            if (score == calcs[3].value) {
                return "bg-green-500";
            } else if (score == calcs[2].value) {
                return "bg-primary";
            } else if (score == calcs[1].value) {
                return "bg-red-500";
            } else if (score == calcs[0].value) {
                return "bg-slate-300";
            } else {
                return "bg-gray-100";
            }
        },

        getScoreTextColorClass(score) {
            if (score >= 80) return "text-green-600";
            if (score >= 60) return "text-primary";
            return "text-red-600";
        },
    },
    mounted() {
        if (this.orgData && this.kvdrData) {
            this.createTalentScorecardData();
        }
    },
};
</script>

<style scoped>
table {
    border-collapse: separate;
    border-spacing: 0;
    table-layout: fixed;
    width: 100%;
}

th,
td {
    border: 1px solid #e5e7eb;
}

th {
    background-color: #f9fafb;
    font-weight: 600;
    color: #4b5563;
}

.name {
    width: 250px !important;
    min-width: 250px !important;
}

td,
th {
    width: 110px;
}
</style>